import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/shared/components/ui/input";
import { Switch } from "@/shared/components/ui/switch";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  DEFAULT_ADMIN_SETTINGS,
  getAdminSettings,
  regenerateAdminSessionTokens,
  resetAdminSettings,
  updateAdminSettings,
} from "@/shared/api/services/adminSettingsService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Settings as SettingsIcon,
  Shield,
  Bell,
  Globe,
  Database,
  Zap,
  Clock,
  Save,
  RefreshCw,
  AlertTriangle,
  Key,
} from "lucide-react";

const C = {
  bg: "#060C18",
  card: "#0E1C35",
  cardHigh: "#122040",
  cardHover: "#162B4D",
  border: "rgba(255,255,255,0.07)",
  borderHover: "rgba(34,211,238,0.35)",
  cyan: "#22D3EE",
  cyanSoft: "rgba(34,211,238,0.08)",
  blue: "#3B82F6",
  blueSoft: "rgba(59,130,246,0.08)",
  emerald: "#10B981",
  amber: "#F59E0B",
  amberSoft: "rgba(245,158,11,0.08)",
  t1: "#EFF6FF",
  t2: "#93B4D4",
  t3: "#4D6E90",
  t4: "#2A4060",
};

const DARK_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

.settings-dark, .settings-dark * {
  font-family: 'Outfit', system-ui, sans-serif !important;
  box-sizing: border-box;
}

.settings-dark input,
.settings-dark textarea {
  background: #0E1C35 !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #EFF6FF !important;
  border-radius: 10px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all .2s !important;
}

.settings-dark input:hover,
.settings-dark textarea:hover {
  border-color: rgba(34,211,238,0.35) !important;
  background: #122040 !important;
}

.settings-dark input:focus,
.settings-dark textarea:focus {
  outline: none !important;
  border-color: rgba(34,211,238,0.5) !important;
  box-shadow: 0 0 0 3px rgba(34,211,238,0.1) !important;
  background: #122040 !important;
}

.settings-dark [data-radix-select-trigger],
.settings-dark [role="combobox"] {
  background: #0E1C35 !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #EFF6FF !important;
  border-radius: 10px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all .2s !important;
}

.settings-dark [data-radix-select-trigger]:hover,
.settings-dark [role="combobox"]:hover {
  border-color: rgba(34,211,238,0.35) !important;
  color: #22D3EE !important;
  background: #122040 !important;
}

.settings-dark [data-radix-select-content] {
  background: #0E1C35 !important;
  border: 1px solid rgba(34,211,238,0.2) !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
}

.settings-dark [data-radix-select-item] {
  color: #93B4D4 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
}

.settings-dark [data-radix-select-item]:hover,
.settings-dark [data-radix-select-item][data-highlighted] {
  background: rgba(34,211,238,0.08) !important;
  color: #22D3EE !important;
}

.settings-dark label {
  color: #93B4D4 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
}

/* Switch styling */
.settings-dark [data-state="checked"] {
  background: linear-gradient(135deg,#22D3EE,#3B82F6) !important;
  box-shadow: 0 0 12px rgba(34,211,238,0.3) !important;
}

.settings-dark [data-state="unchecked"] {
  background: #2A4060 !important;
}

::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: rgba(14,28,53,0.3); border-radius: 10px; }
::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.2); border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: rgba(34,211,238,0.35); }
`;

const tabs = [
  { id: "general", icon: SettingsIcon },
  { id: "security", icon: Shield },
  { id: "callbacks", icon: Zap },
  { id: "qr", icon: Globe },
  { id: "notifications", icon: Bell },
];

function SettingRow({ label, description, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", background: "rgba(255,255,255,0.025)", border: `1px solid ${C.border}`, borderRadius: 12, transition: "all .2s" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(34,211,238,0.2)"; e.currentTarget.style.background = "rgba(34,211,238,0.03)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = "rgba(255,255,255,0.025)"; }}
    >
      <div>
        <p style={{ fontSize: 13, fontWeight: 700, color: C.t1, margin: 0 }}>{label}</p>
        {description && <p style={{ fontSize: 12, color: C.t3, margin: "3px 0 0" }}>{description}</p>}
      </div>
      {children}
    </div>
  );
}

function FormField({ label, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: C.t2, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize: 11, color: C.t3, margin: 0 }}>{hint}</p>}
    </div>
  );
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState("general");
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [isRegenDialogOpen, setIsRegenDialogOpen] = useState(false);

  const [settings, setSettings] = useState(DEFAULT_ADMIN_SETTINGS);
  const [validationErrors, setValidationErrors] = useState({});

  const { data: initialSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: getAdminSettings,
  });

  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
      setValidationErrors({});
    }
  }, [initialSettings]);

  useEffect(() => {
    const id = "settings-dark-css";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = DARK_CSS;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  const isNumericField = (field) => [
    "timestampTolerance",
    "callbackMaxRetries",
    "callbackTimeout",
    "qrDefaultExpiry",
    "qrMinAmount",
    "qrMaxAmount",
    "alertThreshold",
  ].includes(field);

  const parseSafeNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const validateSettings = (next) => {
    const errors = {};

    if (!next.platformName?.trim()) errors.platformName = "Platform name is required";
    if (!next.signatureHeaderName?.trim()) errors.signatureHeaderName = "Signature header is required";
    if (next.timestampTolerance < 0) errors.timestampTolerance = "Must be 0 or greater";
    if (next.callbackMaxRetries < 0) errors.callbackMaxRetries = "Must be 0 or greater";
    if (next.callbackTimeout <= 0) errors.callbackTimeout = "Must be greater than 0";
    if (next.qrDefaultExpiry <= 0) errors.qrDefaultExpiry = "Must be greater than 0";
    if (next.qrMinAmount < 0) errors.qrMinAmount = "Must be 0 or greater";
    if (next.qrMaxAmount <= 0) errors.qrMaxAmount = "Must be greater than 0";
    if (next.qrMaxAmount < next.qrMinAmount) errors.qrMaxAmount = "Max amount must be >= min amount";
    if (next.alertThreshold < 0) errors.alertThreshold = "Must be 0 or greater";
    if (!Array.isArray(next.callbackRetryDelay) || next.callbackRetryDelay.some((n) => !Number.isFinite(n) || n < 0)) {
      errors.callbackRetryDelay = "Use valid comma-separated seconds";
    }

    return errors;
  };

  const handleChange = (field, value) => {
    const nextValue = isNumericField(field) ? parseSafeNumber(value, 0) : value;
    setSettings((prev) => ({ ...prev, [field]: nextValue }));
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const saveMutation = useMutation({
    mutationFn: updateAdminSettings,
    onSuccess: (saved) => {
      const { _savedToBackend, ...cleanSettings } = saved || {};
      queryClient.setQueryData(["admin-settings"], cleanSettings);
      setSettings(cleanSettings);
      setValidationErrors({});
      toast.success(_savedToBackend ? "Settings saved to backend" : "Settings saved locally");
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to save settings");
    },
  });

  const resetMutation = useMutation({
    mutationFn: resetAdminSettings,
    onSuccess: (defaults) => {
      queryClient.setQueryData(["admin-settings"], defaults);
      setSettings(defaults);
      setValidationErrors({});
      toast.success("Settings reset to defaults");
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to reset settings");
    },
  });

  const regenerateSessionMutation = useMutation({
    mutationFn: regenerateAdminSessionTokens,
    onSuccess: () => {
      toast.success("Session tokens regenerated");
      setIsRegenDialogOpen(false);
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to regenerate session tokens");
    },
  });

  const isDirty = useMemo(() => {
    if (!initialSettings) return false;
    return JSON.stringify(settings) !== JSON.stringify(initialSettings);
  }, [settings, initialSettings]);

  const handleSave = async () => {
    const errors = validateSettings(settings);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix validation errors before saving");
      return;
    }

    await saveMutation.mutateAsync(settings);
  };

  const handleReset = async () => {
    await resetMutation.mutateAsync();
  };

  const tabLabels = { general: t("general"), security: t("security"), callbacks: t("callbacks"), qr: t("qrSettings"), notifications: t("notificationsSettings") };

  return (
    <div className="settings-dark min-h-screen" style={{ background: C.bg }}>
      <div aria-hidden className="fixed inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(34,211,238,0.015) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,0.015) 1px,transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse 80% 80% at 50% 30%,black 20%,transparent 100%)" }} />
      <div aria-hidden className="fixed inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 900px 500px at 10% 10%,rgba(34,211,238,0.04) 0%,transparent 70%),radial-gradient(ellipse 700px 400px at 90% 90%,rgba(59,130,246,0.04) 0%,transparent 65%)" }} />

      <div className="relative z-10 max-w-[1100px] mx-auto md:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row mb-8">
          <div className="flex items-center" style={{ gap: 14 }}>
            <div className="flex items-center justify-center" style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,rgba(34,211,238,0.12),rgba(59,130,246,0.12))", border: "1px solid rgba(34,211,238,0.25)", boxShadow: "0 8px 24px rgba(34,211,238,0.12)" }}>
              <SettingsIcon size={22} style={{ color: C.cyan }} />
            </div>
            <div>
              <h1 className="text-[26px] font-extrabold" style={{ color: C.t1, margin: 0, letterSpacing: "-0.5px" }}>{t("settingsTitle")}</h1>
              <p className="text-sm" style={{ color: C.t3, margin: "3px 0 0", fontWeight: 500 }}>{t("platformConfig")}</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={isLoadingSettings || saveMutation.isPending || resetMutation.isPending || !isDirty}
            className="inline-flex items-center gap-1 py-2 px-4 rounded-lg text-xs font-bold transition"
            style={{ cursor: saveMutation.isPending || resetMutation.isPending || !isDirty ? "not-allowed" : "pointer", border: "1px solid rgba(34,211,238,0.3)", background: "linear-gradient(135deg,rgba(34,211,238,0.12),rgba(59,130,246,0.12))", color: C.cyan, boxShadow: "0 4px 16px rgba(34,211,238,0.12)", opacity: saveMutation.isPending || resetMutation.isPending || !isDirty ? 0.7 : 1 }}
            onMouseEnter={(e) => { if (!saveMutation.isPending && !resetMutation.isPending && isDirty) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(34,211,238,0.25)"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.5)"; } }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,211,238,0.12)"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.3)"; }}
          >
            {saveMutation.isPending ? <RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={14} />}
            {saveMutation.isPending ? t("saving") : t("saveChanges")}
          </button>
        </div>

        {Object.keys(validationErrors).length > 0 && (
          <div style={{ marginBottom: 16, padding: 12, borderRadius: 10, border: "1px solid rgba(245,158,11,0.35)", background: "rgba(245,158,11,0.08)", color: C.amber, fontSize: 12, fontWeight: 600 }}>
            Please fix the highlighted fields before saving.
          </div>
        )}

        <div className="flex flex-col justify-center gap-10">
          {/* Sidebar Tabs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {tabs.map(({ id, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-semibold transition"
                style={{ border: `1px solid ${activeTab === id ? "rgba(34,211,238,0.3)" : "transparent"}`, background: activeTab === id ? "rgba(34,211,238,0.1)" : "transparent", color: activeTab === id ? C.cyan : C.t3 }}
                onMouseEnter={(e) => { if (activeTab !== id) { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = C.t2; } }}
                onMouseLeave={(e) => { if (activeTab !== id) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.t3; } }}
              >
                <Icon size={15} />
                {tabLabels[id]}
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <motion.div key={activeTab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
              <div style={{ background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`, border: `1px solid ${C.border}`, borderRadius: 18, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.2)", position: "relative" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.12),transparent)" }} />
                <div style={{ padding: "22px 24px", borderBottom: `1px solid ${C.border}` }}>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: C.t1, margin: 0 }}>
                    {activeTab === "general" && t("generalSettings")}
                    {activeTab === "security" && t("securitySettings")}
                    {activeTab === "callbacks" && t("callbackSettings")}
                    {activeTab === "qr" && t("qrPaymentSettings")}
                    {activeTab === "notifications" && t("notificationSettings")}
                  </h2>
                </div>

                <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
                  {/* General */}
                  {activeTab === "general" && <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <FormField label={t("platformName")}>
                        <Input value={settings.platformName} onChange={(e) => handleChange("platformName", e.target.value)} />
                        {validationErrors.platformName && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.platformName}</p>}
                      </FormField>
                      <FormField label={t("timezone")}>
                        <Select value={settings.timezone} onValueChange={(v) => handleChange("timezone", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Asia/Bangkok">Asia/Bangkok (ICT)</SelectItem>
                            <SelectItem value="UTC">UTC</SelectItem>
                            <SelectItem value="Asia/Singapore">Asia/Singapore</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormField>
                    </div>
                    <FormField label={t("defaultCurrency")}>
                      <div style={{ maxWidth: 220 }}>
                        <Select value={settings.defaultCurrency} onValueChange={(v) => handleChange("defaultCurrency", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="THB">THB - Thai Baht</SelectItem>
                            <SelectItem value="USD">USD - US Dollar</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </FormField>
                  </>}

                  {/* Security */}
                  {activeTab === "security" && <>
                    <SettingRow label={t("requireIpWhitelist")} description={t("acceptApiCallsFromWhitelistedIps")}>
                      <Switch checked={settings.requireIPWhitelist} onCheckedChange={(v) => handleChange("requireIPWhitelist", v)} />
                    </SettingRow>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <FormField label={t("hmacAlgorithm")}>
                        <Select value={settings.hmacAlgorithm} onValueChange={(v) => handleChange("hmacAlgorithm", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sha256">SHA-256</SelectItem>
                            <SelectItem value="sha512">SHA-512</SelectItem>
                            <SelectItem value="sha1">SHA-1 (legacy)</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormField>
                      <FormField label={t("signatureHeaderName")}>
                        <Input value={settings.signatureHeaderName} onChange={(e) => handleChange("signatureHeaderName", e.target.value)} />
                        {validationErrors.signatureHeaderName && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.signatureHeaderName}</p>}
                      </FormField>
                    </div>
                    <FormField label={t("timestampTolerance")} hint={t("rejectOldRequests")}>
                      <Input type="number" value={settings.timestampTolerance} onChange={(e) => handleChange("timestampTolerance", parseInt(e.target.value))} style={{ maxWidth: 220 }} />
                      {validationErrors.timestampTolerance && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.timestampTolerance}</p>}
                    </FormField>
                    <SettingRow
                      label="Session Token Regeneration"
                      description="Regenerate admin access/refresh tokens using backend refresh flow"
                    >
                      <button
                        onClick={() => setIsRegenDialogOpen(true)}
                        disabled={regenerateSessionMutation.isPending}
                        className="inline-flex items-center gap-1 py-1.5 px-3 rounded-lg text-xs font-bold transition"
                        style={{
                          cursor: regenerateSessionMutation.isPending ? "not-allowed" : "pointer",
                          border: "1px solid rgba(34,211,238,0.3)",
                          background: "rgba(34,211,238,0.1)",
                          color: C.cyan,
                          opacity: regenerateSessionMutation.isPending ? 0.7 : 1,
                        }}
                      >
                        {regenerateSessionMutation.isPending ? (
                          <RefreshCw size={12} style={{ animation: "spin 1s linear infinite" }} />
                        ) : (
                          <Key size={12} />
                        )}
                        Regenerate
                      </button>
                    </SettingRow>
                  </>}

                  {/* Callbacks */}
                  {activeTab === "callbacks" && <>
                    <SettingRow label={t("enableCallbackRetry")} description={t("autoRetryFailedCallbacks")}>
                      <Switch checked={settings.callbackRetryEnabled} onCheckedChange={(v) => handleChange("callbackRetryEnabled", v)} />
                    </SettingRow>
                    {settings.callbackRetryEnabled && <>
                      <FormField label={t("maxRetryAttempts")}>
                        <Input type="number" value={settings.callbackMaxRetries} onChange={(e) => handleChange("callbackMaxRetries", parseInt(e.target.value))} style={{ maxWidth: 200 }} />
                        {validationErrors.callbackMaxRetries && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.callbackMaxRetries}</p>}
                      </FormField>
                      <FormField label={t("retryDelaySchedule")} hint={t("settingRetryDelayScheduleNote")}>
                        <Input value={settings.callbackRetryDelay.join(", ")} onChange={(e) => handleChange("callbackRetryDelay", e.target.value.split(",").map(v => parseSafeNumber(v.trim(), NaN)).filter(Number.isFinite))} />
                        {validationErrors.callbackRetryDelay && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.callbackRetryDelay}</p>}
                      </FormField>
                    </>}
                    <FormField label={t("callbackTimeout")}>
                      <Input type="number" value={settings.callbackTimeout} onChange={(e) => handleChange("callbackTimeout", parseInt(e.target.value))} style={{ maxWidth: 200 }} />
                      {validationErrors.callbackTimeout && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.callbackTimeout}</p>}
                    </FormField>
                  </>}

                  {/* QR */}
                  {activeTab === "qr" && <>
                    <FormField label={t("defaultQrExpiry")}>
                      <Input type="number" value={settings.qrDefaultExpiry} onChange={(e) => handleChange("qrDefaultExpiry", parseInt(e.target.value))} style={{ maxWidth: 200 }} />
                      {validationErrors.qrDefaultExpiry && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.qrDefaultExpiry}</p>}
                    </FormField>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <FormField label={t("minimumAmount")}>
                        <Input type="number" value={settings.qrMinAmount} onChange={(e) => handleChange("qrMinAmount", parseInt(e.target.value))} />
                        {validationErrors.qrMinAmount && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.qrMinAmount}</p>}
                      </FormField>
                      <FormField label={t("maximumAmount")}>
                        <Input type="number" value={settings.qrMaxAmount} onChange={(e) => handleChange("qrMaxAmount", parseInt(e.target.value))} />
                        {validationErrors.qrMaxAmount && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.qrMaxAmount}</p>}
                      </FormField>
                    </div>
                    <div style={{ padding: 16, background: C.blueSoft, border: "1px solid rgba(59,130,246,0.25)", borderRadius: 12 }}>
                      <div style={{ display: "flex", gap: 10 }}>
                        <Globe size={18} style={{ color: C.blue, flexShrink: 0, marginTop: 1 }} />
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 700, color: C.blue, margin: "0 0 4px" }}>{t("supportedBanks")}</p>
                          <p style={{ fontSize: 12, color: C.t2, margin: 0 }}>SCB, KBank, BBL, Krungsri, TTB – {t("enableInProviderSettings")}</p>
                        </div>
                      </div>
                    </div>
                  </>}

                  {/* Notifications */}
                  {activeTab === "notifications" && <>
                    <SettingRow label={t("emailNotifications")} description={t("sendAlertsViaEmail")}>
                      <Switch checked={settings.emailNotifications} onCheckedChange={(v) => handleChange("emailNotifications", v)} />
                    </SettingRow>
                    <SettingRow label={t("slackNotifications")} description={t("sendAlertsToSlack")}>
                      <Switch checked={settings.slackNotifications} onCheckedChange={(v) => handleChange("slackNotifications", v)} />
                    </SettingRow>
                    <FormField label={t("alertThreshold")} hint={t("sendAlertAboveAmount")}>
                      <Input type="number" value={settings.alertThreshold} onChange={(e) => handleChange("alertThreshold", parseInt(e.target.value))} style={{ maxWidth: 220 }} />
                      {validationErrors.alertThreshold && <p style={{ color: "#f59e0b", fontSize: 11, margin: "4px 0 0" }}>{validationErrors.alertThreshold}</p>}
                    </FormField>
                    <div style={{ padding: 16, background: C.amberSoft, border: "1px solid rgba(245,158,11,0.25)", borderRadius: 12 }}>
                      <div style={{ display: "flex", gap: 10 }}>
                        <AlertTriangle size={18} style={{ color: C.amber, flexShrink: 0, marginTop: 1 }} />
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 700, color: C.amber, margin: "0 0 8px" }}>{t("alertTypes")}</p>
                          {[t("highValueTransactions"), t("providerFailures"), t("failedCallbackRetries"), t("suspiciousActivity")].map(item => (
                            <p key={item} style={{ fontSize: 12, color: C.t2, margin: "0 0 4px" }}>• {item}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <AlertDialog open={isRegenDialogOpen} onOpenChange={setIsRegenDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate Session Tokens</AlertDialogTitle>
            <AlertDialogDescription>
              This will call the backend refresh flow and rotate your active admin session tokens.
              Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                regenerateSessionMutation.mutate();
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}